interface WorkExperienceItem {
  company: string;
  position: string;
  period: string;
  description?: string;
  achievements: string[];
  technologies: string;
  skills?: string;
  credentialSkills?: string[];
  research?: string;
  viewMore?: string;
}

function WorkExperience(): JSX.Element {
  const experiences: WorkExperienceItem[] = [
    {
      company: "Fuel2",
      position: "AI Solutions Architect & Full-Stack Engineer",
      period: "Jan 2025 - Present",
      description: "Architecting and building Fuel2, a multi-agent AI platform that helps teams transform product requirements and domain knowledge into structured software designs and implementation workflows. Applying artificial intelligence, enterprise integration, suitability analysis, and emerging technologies to create practical AI-assisted solutions while combining Domain-Driven Design, CQRS, event-driven architecture, and full-stack engineering across domain models, APIs, workflows, and user interfaces.",
      achievements: [
        "Multi-Agent AI Framework Development: Architected and implemented fuel2-chat, a ChatDev-inspired multi-agent AI collaboration framework for automated software development scaffolding, leveraging Domain-Driven Design (DDD) principles and microservices architecture.",
        "Microservices Architecture: Designed and developed 5 bounded contexts (Agent Collaboration, Workflow Execution, Artifact Production, Domain Modeling, Framework Infrastructure) with clear separation of concerns, implementing CQRS and Event-Driven Architecture patterns.",
        "Full-Stack Development: Built comprehensive web UI using React, TypeScript, and Vite with shadcn/ui component library. Developed backend services using Python FastAPI with proper domain modeling, value objects, and aggregates following DDD tactical patterns.",
        "DevOps & Infrastructure: Set up complete local development stack using Colima and Docker Compose with PostgreSQL (multi-schema), Redis, Kafka, Prometheus, Grafana, OpenTelemetry Collector, MinIO (S3-compatible storage), and Bugsink (error tracking).",
        "API Gateway Implementation: Designed and deployed Nginx-based API Gateway for centralized credential management, rate limiting, circuit breaking, and unified error handling for external service integrations.",
        "Observability & Monitoring: Implemented comprehensive observability stack with distributed tracing (OpenTelemetry), metrics collection (Prometheus), visualization dashboards (Grafana), and error tracking (Sentry/Bugsink integration).",
        "Testing Strategy: Established E2E testing framework with Cypress, implemented TDD workflows, created comprehensive test suites (unit, contract, integration tests), and developed automated verification scripts for platform health checks.",
        "Technical Documentation: Created extensive ADRs (Architecture Decision Records), maintained project documentation, developed agent handoff guides, and produced comprehensive implementation guides for complex features."
      ],
      technologies: "Python, FastAPI, React, TypeScript, Vite, PostgreSQL, Redis, Kafka, Docker, Kubernetes, Nginx, Prometheus, Grafana, OpenTelemetry, MinIO, Sentry, Cypress, shadcn/ui, TailwindCSS",
      skills: "Domain-Driven Design (DDD), CQRS, Event-Driven Architecture, Microservices, Multi-Agent Systems, DevOps, CI/CD, Technical Leadership",
      credentialSkills: [
        "Artificial Intelligence (AI)",
        "Business Efficiency",
        "Career Management",
        "Collaboration",
        "Critical Thinking",
        "Decision Making",
        "Emerging Technologies",
        "Enterprise Integration",
        "Organizational Effectiveness",
        "Planning",
        "Suitability Analysis",
        "Written Communication Skills"
      ],
      research: "Researched and implemented AI agent orchestration patterns, ChatChain coordination mechanisms, BDD/TDD-driven development workflows, and autonomous software scaffolding systems"
    },
    {
      company: "SenPrints",
      position: "Software Engineer",
      period: "April 2021 - Dec 2024",
      achievements: [
        "Optimized cloud infrastructure: reducing AWS costs 60% ($130/day to $50/day) using Kubernetes and hybrid model of cloud/selfhosted, Terraform & microservices. Set up CI/CD pipelines for development, testing, and production environments.",
        "Web application development: Built component logic and UI from Figma designs, ensured pixel-perfect precision, handled responsiveness, and conducted comprehensive testing. Experienced in building complex, interactive UI components using Phaser.js, delivering dynamic and user-friendly interfaces.",
        "Software architecture: Experienced in designing and implementing scalable, maintainable, and modular software systems using modern architectural principles with popular patterns like Competing consumers, Split-aggregate, Conversation identifier, Claim-check",
        "DDD, CQRS, and Event-Driven Architecture: Ensured clear separation of concerns, high reusability, and flexibility in system design. I also have experience with SOLID, clean and efficient coding practices.",
        "Team Enablement: Maintained project documentation, diagrams, and automated test scenarios to ensure seamless knowledge transfer and enable new technical teams to quickly understand and expand existing systems. Set up and synchronized local development environments for the team, ensuring consistency and reducing onboarding time.",
        "Performance Optimization & Monitoring: Reduced image rendering times by 92% (20 seconds to 1.6 seconds) without caching. By profiling and measuring, I identified which parts of the image rendering process were the slowest or most resource-intensive. I then focused on resolving the bottlenecks that were both easy to fix and had the greatest impact on performance. This method helps in achieving quick wins and noticeable improvements without getting stuck on more complicated/less impacted issues.",
        "Created performance dashboards, automated scaling during traffic peaks, and optimized AWS costs. Also, set up a home server room with Kubernetes cluster, solar-powered for cost reduction.",
        "Profiled and reviewed the infrastructure topology of the main product of company. By this, I was able to optimize the system and successfully reduce the response time from 1.9 seconds to 1.1 seconds – all without modifying the codebase."
      ],
      technologies: "AWS, Kubernetes, Linux, Terraform, Docker, Gitlab CI/CD, Vuejs, Cypress, Phaser.js, Python, PHP/Laravel, Node.js (ExpressJS), Redis, Varnish, CSS, HTML",
      skills: "JIRA, problem-solving & analytical thinking, adaptability",
      research: "Researched and experimented for Supabase, GraphQL, Kafka and RabbitMQ",
      viewMore: "https://docs.google.com/document/d/1IONI6IZfRNK1O0SwZKla6jifRLXMfXfIOmIDiEw61Xw/edit?usp=sharing"
    },
    {
      company: "Zotabox",
      position: "Software Engineer",
      period: "November 2019 - March 2021",
      achievements: [
        "Developed & Scaled Marketing Toolkit: Contributed to the development of a comprehensive marketing toolkit offering customizable tools, boosting customer engagement for thousands of users globally.",
        "Security Enhancements: Identified and resolved critical SQL security issues, ensuring the platform remained safe and reliable for businesses using the toolkit.",
        "UI/UX Improvements: Collaborated with cross-functional teams to deliver a smooth, responsive user experience, optimizing the interface for ease of use and increased conversion rates.",
        "Performance Optimization: Integrated Object-Relational Mapping (ORM) to improve database management without impacting performance. Improved Docker-based shared environments for smoother collaboration.",
        "Testing & Automation: Implemented automated testing for all new features, significantly reducing the occurrence of bugs in production."
      ],
      technologies: "Vue2, Cypress, Laravel, Node.js, Docker, HTML, CSS",
      viewMore: "https://docs.google.com/document/d/1dsDoslUn6kAoWH5tMv4Gt037MZFFoeu5RhvyAJyuQtk/edit?usp=sharing"
    },
    {
      company: "FPTSoftware",
      position: "Software Engineer",
      period: "August 2018 - August 2019",
      achievements: [
        "FPT Software's new website for multiple languages ​​and countries - Project www.fpt-software.com: Works with existing codebase, integrates WordPress Multilingual to deliver content faster. Maintain WordPress websites, provide website-related technical support to FPT Software's marketing departments in various countries.",
        "Develop brand new website for FPT DigitalAcademy - Project digitalacademy.fpt.com.vn: Creating pages from pre-designed PSD using WordPress allows admins to manage content like News, Events, ...",
        "Project gsshop.vn - GS Group [Korea]: Developing Reward Point on Magento2 with Earning Rules: Customers can earn points for various activities such as making purchases. These earned points can then be redeemed for discounts on future orders. I have experience carefully reviewing source code for colleagues. Allow admins to configure custom rules to determine how many points customers earn for specific actions. Provide a user-friendly interface for customers to view their point balance and transaction history. Allow customers to redeem points for discounts on their orders."
      ],
      technologies: "PHP, Magento2, Docker, WordPress, CSS, HTML, AngularJS"
    },
    {
      company: "EGDGroup",
      position: "Software Engineer",
      period: "July 2016 - August 2018",
      achievements: [
        "SACWireless Bidding System: Designed the database schema and data flow diagram to optimize the bidding system, ensuring efficient data handling and retrieval. Worked closely with a team of developers to implement, test, and deploy the entire system, ensuring seamless integration of all modules. Quickly adapted to new requirements of project. Built a user-friendly, interactive calendar, ensuring a smooth user experience. Setting up development and production environments on AWS. Setup CI/CD pipelines with Build, Test, Deploy phases for services",
        "AeroDirect: Develop UI for RFQ form use existing API. Develop filter of machine parts use existing API",
        "WebForge: Develop core modules for Drag-n-Drop web builder use AngularJS. Develop Page Builder, Popup Builder and other modules based on core module. Integrate modules into the system"
      ],
      technologies: "AngularJS, PHP, AWS, Laravel, OOP, RESTful APIs, SOAP API, Authentication, Access Management",
      skills: "Trello, Agile/Scrum, problem-solving & analytical thinking, collaboration, adaptability"
    }
  ];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-8">Work Experience</h2>
      <div className="relative">
        <div className="timeline-line absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-500 hidden md:block"></div>
        <div className="space-y-10">
          {experiences.map((exp: WorkExperienceItem, index: number) => (
            <div key={index} className="relative pl-0 md:pl-12">
              <div className="absolute left-2 top-2 w-4 h-4 bg-white border-4 border-blue-500 rounded-full shadow-lg hidden md:block"></div>
              
              <div className=" rounded-xl transition-all duration-300 p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-1">{exp.position}</h3>
                    <p className="text-lg text-blue-600 font-semibold mb-1">{exp.company}</p>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      📅 {exp.period}
                    </div>
                  </div>
                </div>

                {exp.description && (
                  <p className="mb-6 rounded-lg border border-blue-100 bg-blue-50 p-4 leading-relaxed text-gray-700">
                    {exp.description}
                  </p>
                )}
                
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Key Achievements</h4>
                  <ul className="space-y-3">
                    {exp.achievements.map((achievement: string, achIndex: number) => (
                      <li key={achIndex} className="flex items-start space-x-3">
                        <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                        <span className="text-gray-700 leading-relaxed">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center">
                      <span className="mr-2">🛠️</span>
                      Technologies
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.split(', ').map((tech: string, techIndex: number) => (
                        <span key={techIndex} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {exp.skills && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <span className="mr-2">💡</span>
                        Skills Applied
                      </h5>
                      <div className="flex flex-wrap gap-2">
                        {exp.skills.split(', ').map((skill: string, skillIndex: number) => (
                          <span key={skillIndex} className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {exp.credentialSkills && (
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h5 className="mb-2 flex items-center font-semibold text-blue-900">
                      <span className="mr-2">🎓</span>
                      IBM SkillsBuild Skills Applied at Fuel2
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {exp.credentialSkills.map((skill: string) => (
                        <span
                          key={skill}
                          className="rounded-md border border-blue-200 bg-white px-2 py-1 text-xs font-medium text-blue-800"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {exp.research && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <h5 className="font-semibold text-yellow-800 mb-2 flex items-center">
                      <span className="mr-2">🔬</span>
                      Research & Development
                    </h5>
                    <p className="text-yellow-700 text-sm">{exp.research}</p>
                  </div>
                )}
                
                {exp.viewMore && (
                  <div className="flex justify-end">
                    <a 
                      href={exp.viewMore} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium shadow-md hover:shadow-lg"
                    >
                      <span className="mr-2">📄</span>
                      View Detailed Report
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WorkExperience;
